import { ChevronDown } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@predoc/cow';
import { cn } from '@/lib/utils';
import {
  isFs2Facesheet,
  type FacesheetImage,
  type FacesheetVersion,
  type ViewFacesheetSummaryParams,
} from './types';

/** Keeps FS1.0 (View▾) and FS2.0 (View) the same footprint in the analysis table. */
const VIEW_BUTTON_WIDTH_CLASS = 'w-20';

const hasAnySignedUrl = (items: FacesheetImage[]): boolean =>
  items.some((item) => item.signedUrl);

interface ViewFacesheetAssetsMenuProps {
  facesheetRequestId?: string;
  facesheetName?: string;
  version?: FacesheetVersion;
  resultsSummaries: FacesheetImage[];
  sources: FacesheetImage[];
  onViewImage: (signedUrl: string) => void;
  onViewFacesheet: (params: ViewFacesheetSummaryParams) => void;
}

const Fs1ViewMenu = ({
  resultsSummaries,
  sources,
  onViewImage,
}: Pick<
  ViewFacesheetAssetsMenuProps,
  'resultsSummaries' | 'sources' | 'onViewImage'
>) => {
  const showSummarySection = resultsSummaries.length > 0;
  const showSourcesSection = sources.length > 0;

  const hasValidImages =
    hasAnySignedUrl(resultsSummaries) || hasAnySignedUrl(sources);

  if (!showSummarySection && !showSourcesSection) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className={VIEW_BUTTON_WIDTH_CLASS}
      >
        View
      </Button>
    );
  }

  if (!hasValidImages) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className={VIEW_BUTTON_WIDTH_CLASS}
      >
        View
      </Button>
    );
  }

  const handleItemSelect = (signedUrl?: string) => {
    if (signedUrl) {
      onViewImage(signedUrl);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(VIEW_BUTTON_WIDTH_CLASS, 'gap-1')}
        >
          View
          <ChevronDown className="size-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        className="max-h-[280px] overflow-y-auto"
      >
        {showSummarySection &&
          resultsSummaries.map((item, index) => (
            <DropdownMenuItem
              key={item.id}
              disabled={!item.signedUrl}
              onSelect={() => handleItemSelect(item.signedUrl)}
              className="font-medium"
              aria-label={`Summary ${index + 1}`}
            >
              Summary {index + 1}
            </DropdownMenuItem>
          ))}
        {showSummarySection && showSourcesSection && <DropdownMenuSeparator />}
        {showSourcesSection && (
          <div className="px-2 py-1.5 text-sm font-semibold">Sources</div>
        )}
        {showSourcesSection &&
          sources.map((item, index) => (
            <DropdownMenuItem
              key={item.id}
              disabled={!item.signedUrl}
              onSelect={() => handleItemSelect(item.signedUrl)}
              className="font-medium"
              aria-label={`Source ${index + 1}`}
            >
              Source {index + 1}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Fs2ViewButton = ({
  facesheetRequestId,
  facesheetName,
  onViewFacesheet,
}: Pick<
  ViewFacesheetAssetsMenuProps,
  'facesheetRequestId' | 'facesheetName' | 'onViewFacesheet'
>) => {
  const handleView = () => {
    if (!facesheetRequestId) return;

    onViewFacesheet({
      facesheetRequestId,
      facesheetName,
    });
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleView}
      disabled={!facesheetRequestId}
      className={VIEW_BUTTON_WIDTH_CLASS}
    >
      View
    </Button>
  );
};

export const ViewFacesheetAssetsMenu = ({
  facesheetRequestId,
  facesheetName,
  version,
  resultsSummaries,
  sources,
  onViewImage,
  onViewFacesheet,
}: ViewFacesheetAssetsMenuProps) => {
  const isFs2 = isFs2Facesheet({ version: version ?? 'UNKNOWN' });

  if (isFs2) {
    return (
      <Fs2ViewButton
        facesheetRequestId={facesheetRequestId}
        facesheetName={facesheetName}
        onViewFacesheet={onViewFacesheet}
      />
    );
  }

  return (
    <Fs1ViewMenu
      resultsSummaries={resultsSummaries}
      sources={sources}
      onViewImage={onViewImage}
    />
  );
};
